import React, { createContext, useState, useEffect } from "react";
import { tokenStorage } from "@/utils/tokenStorage";
import { logger } from "@/utils/logger";

// Define User type directly with expected properties
type User = {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  superuser?: boolean;
  roles?: string[];
  disabled?: boolean;
};

// Export AuthContextType
export interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

// Export AuthContext
export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({
  children,
  apiBaseUrl,
}: {
  children: React.ReactNode;
  apiBaseUrl: string;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return tokenStorage.getToken();
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Initialize isLoading to true

  // Function to fetch user data
  const fetchUserData = async () => {
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const url = `${apiBaseUrl}/users/me`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        logger.info("User data refreshed successfully");
      } else {
        logger.warn("Invalid authentication token, logging out user");
        logout();
      }
    } catch (error) {
      logger.error("Error fetching user data", {
        error: (error as Error).message,
        apiUrl: apiBaseUrl,
      });
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle login
  const login = (newToken: string) => {
    setIsLoading(true);
    tokenStorage.setToken(newToken);
    setToken(newToken);
  };

  // Function to handle logout
  const logout = () => {
    tokenStorage.removeToken();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false); // Set loading false on logout
    logger.info("User logged out");
  };

  // Effect to fetch user data when token changes
  useEffect(() => {
    fetchUserData();
  }, [token]);

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        token, 
        isAuthenticated, 
        isLoading, 
        login, 
        logout,
        refreshUser: fetchUserData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
